// =============================================================================
// BrightPath - Health UI State Store (Zustand)
// Package: BP-03-04-P1 (Health Frontend - Foundation)
// =============================================================================

import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { HealthLogTypeSlug } from '@/features/health/types/healthTypes';

// =============================================================================
// Types
// =============================================================================

export type HealthViewMode = 'list' | 'timeline' | 'calendar';
export type HealthDatePreset = '7d' | '14d' | '30d' | '90d' | 'custom';

export interface DateRange {
  start: Date;
  end: Date;
  preset: HealthDatePreset;
}

export interface HealthFilters {
  logTypes: string[];
  severityMin: number | null;
  severityMax: number | null;
  searchQuery: string;
  showOnlyWithNotes: boolean;
  showOnlyWithAttachments: boolean;
}

export interface QuickAddState {
  isOpen: boolean;
  logType: HealthLogTypeSlug | null;
  prefillData: Record<string, unknown> | null;
}

export interface MedicationViewFilters {
  showActive: boolean;
  showDiscontinued: boolean;
  searchQuery: string;
}

export interface HealthStore {
  // ===================
  // Health Logs State
  // ===================
  
  /** Currently selected log type filter (null = all types) */
  selectedLogType: string | null;
  
  /** Current date range for viewing logs */
  dateRange: DateRange;
  
  /** Current view mode (list, timeline, calendar) */
  viewMode: HealthViewMode;
  
  /** Advanced filters */
  filters: HealthFilters;
  
  /** Currently selected log ID (for detail view) */
  selectedLogId: string | null;
  
  // ===================
  // Quick Add State
  // ===================
  
  /** Quick add modal state */
  quickAdd: QuickAddState;
  
  // ===================
  // Medications State
  // ===================
  
  /** Medication view filters */
  medicationFilters: MedicationViewFilters;
  
  /** Currently selected medication ID */
  selectedMedicationId: string | null;
  
  /** Show medication reminder modal */
  showMedicationReminder: boolean;
  
  /** Pending medication reminder data */
  pendingMedicationReminder: {
    medicationId: string;
    scheduledTime: string;
  } | null;
  
  // ===================
  // Analytics State
  // ===================
  
  /** Analytics chart type selection */
  analyticsChartType: 'seizures' | 'sleep' | 'mood' | 'medications' | 'all';
  
  /** Analytics comparison enabled */
  analyticsCompareEnabled: boolean;
  
  // ===================
  // Export State
  // ===================
  
  /** Export modal open state */
  isExportModalOpen: boolean;
  
  // ===================
  // Actions - Health Logs
  // ===================
  
  setSelectedLogType: (type: string | null) => void;
  setDateRange: (range: Partial<DateRange>) => void;
  setDateRangePreset: (preset: HealthDatePreset) => void;
  setViewMode: (mode: HealthViewMode) => void;
  setFilters: (filters: Partial<HealthFilters>) => void;
  resetFilters: () => void;
  setSelectedLogId: (id: string | null) => void;
  
  // ===================
  // Actions - Quick Add
  // ===================
  
  openQuickAdd: (logType?: HealthLogTypeSlug, prefillData?: Record<string, unknown>) => void;
  closeQuickAdd: () => void;
  setQuickAddType: (logType: HealthLogTypeSlug) => void;
  
  // ===================
  // Actions - Medications
  // ===================
  
  setMedicationFilters: (filters: Partial<MedicationViewFilters>) => void;
  resetMedicationFilters: () => void;
  setSelectedMedicationId: (id: string | null) => void;
  showMedicationReminderModal: (medicationId: string, scheduledTime: string) => void;
  hideMedicationReminderModal: () => void;
  
  // ===================
  // Actions - Analytics
  // ===================
  
  setAnalyticsChartType: (type: HealthStore['analyticsChartType']) => void;
  toggleAnalyticsCompare: () => void;
  
  // ===================
  // Actions - Export
  // ===================
  
  openExportModal: () => void;
  closeExportModal: () => void;
  
  // ===================
  // Actions - Reset
  // ===================
  
  resetAll: () => void;
}

// =============================================================================
// Default Values
// =============================================================================

const getDefaultDateRange = (): DateRange => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  return { start, end, preset: '30d' };
};

const defaultFilters: HealthFilters = {
  logTypes: [],
  severityMin: null,
  severityMax: null,
  searchQuery: '',
  showOnlyWithNotes: false,
  showOnlyWithAttachments: false,
};

const defaultMedicationFilters: MedicationViewFilters = {
  showActive: true,
  showDiscontinued: false,
  searchQuery: '',
};

const defaultQuickAdd: QuickAddState = {
  isOpen: false,
  logType: null,
  prefillData: null,
};

// =============================================================================
// Store Implementation
// =============================================================================

export const useHealthStore = create<HealthStore>()(
  devtools(
    persist(
      immer((set) => ({
        // ===================
        // Initial State
        // ===================
        
        selectedLogType: null,
        dateRange: getDefaultDateRange(),
        viewMode: 'list',
        filters: defaultFilters,
        selectedLogId: null,
        
        quickAdd: defaultQuickAdd,
        
        medicationFilters: defaultMedicationFilters,
        selectedMedicationId: null,
        showMedicationReminder: false,
        pendingMedicationReminder: null,
        
        analyticsChartType: 'all',
        analyticsCompareEnabled: true,
        
        isExportModalOpen: false,
        
        // ===================
        // Actions - Health Logs
        // ===================
        
        setSelectedLogType: (type) =>
          set((state) => {
            state.selectedLogType = type;
          }),
        
        setDateRange: (range) =>
          set((state) => {
            if (range.start) state.dateRange.start = range.start;
            if (range.end) state.dateRange.end = range.end;
            if (range.preset) state.dateRange.preset = range.preset;
          }),
        
        setDateRangePreset: (preset) =>
          set((state) => {
            const end = new Date();
            const start = new Date();
            
            switch (preset) {
              case '7d':
                start.setDate(start.getDate() - 7);
                break;
              case '14d':
                start.setDate(start.getDate() - 14);
                break;
              case '30d':
                start.setDate(start.getDate() - 30);
                break;
              case '90d':
                start.setDate(start.getDate() - 90);
                break;
              case 'custom':
                // Keep current dates for custom
                return;
            }
            
            state.dateRange = { start, end, preset };
          }),
        
        setViewMode: (mode) =>
          set((state) => {
            state.viewMode = mode;
          }),
        
        setFilters: (filters) =>
          set((state) => {
            Object.assign(state.filters, filters);
          }),
        
        resetFilters: () =>
          set((state) => {
            state.filters = defaultFilters;
            state.selectedLogType = null;
          }),
        
        setSelectedLogId: (id) =>
          set((state) => {
            state.selectedLogId = id;
          }),
        
        // ===================
        // Actions - Quick Add
        // ===================
        
        openQuickAdd: (logType, prefillData) =>
          set((state) => {
            state.quickAdd = {
              isOpen: true,
              logType: logType ?? null,
              prefillData: prefillData ?? null,
            };
          }),
        
        closeQuickAdd: () =>
          set((state) => {
            state.quickAdd = defaultQuickAdd;
          }),
        
        setQuickAddType: (logType) =>
          set((state) => {
            state.quickAdd.logType = logType;
          }),
        
        // ===================
        // Actions - Medications
        // ===================
        
        setMedicationFilters: (filters) =>
          set((state) => {
            Object.assign(state.medicationFilters, filters);
          }),
        
        resetMedicationFilters: () =>
          set((state) => {
            state.medicationFilters = defaultMedicationFilters;
          }),
        
        setSelectedMedicationId: (id) =>
          set((state) => {
            state.selectedMedicationId = id;
          }),
        
        showMedicationReminderModal: (medicationId, scheduledTime) =>
          set((state) => {
            state.showMedicationReminder = true;
            state.pendingMedicationReminder = { medicationId, scheduledTime };
          }),
        
        hideMedicationReminderModal: () =>
          set((state) => {
            state.showMedicationReminder = false;
            state.pendingMedicationReminder = null;
          }),
        
        // ===================
        // Actions - Analytics
        // ===================
        
        setAnalyticsChartType: (type) =>
          set((state) => {
            state.analyticsChartType = type;
          }),
        
        toggleAnalyticsCompare: () =>
          set((state) => {
            state.analyticsCompareEnabled = !state.analyticsCompareEnabled;
          }),
        
        // ===================
        // Actions - Export
        // ===================
        
        openExportModal: () =>
          set((state) => {
            state.isExportModalOpen = true;
          }),
        
        closeExportModal: () =>
          set((state) => {
            state.isExportModalOpen = false;
          }),
        
        // ===================
        // Actions - Reset
        // ===================
        
        resetAll: () =>
          set((state) => {
            state.selectedLogType = null;
            state.dateRange = getDefaultDateRange();
            state.viewMode = 'list';
            state.filters = defaultFilters;
            state.selectedLogId = null;
            state.quickAdd = defaultQuickAdd;
            state.medicationFilters = defaultMedicationFilters;
            state.selectedMedicationId = null;
            state.showMedicationReminder = false;
            state.pendingMedicationReminder = null;
            state.analyticsChartType = 'all';
            state.analyticsCompareEnabled = true;
            state.isExportModalOpen = false;
          }),
      })),
      {
        name: 'brightpath-health-store',
        partialize: (state) => ({
          // Only persist user preferences, not transient UI state
          viewMode: state.viewMode,
          analyticsChartType: state.analyticsChartType,
          analyticsCompareEnabled: state.analyticsCompareEnabled,
          medicationFilters: {
            showActive: state.medicationFilters.showActive,
            showDiscontinued: state.medicationFilters.showDiscontinued,
          },
        }),
      }
    ),
    { name: 'HealthStore' }
  )
);

// =============================================================================
// Selectors (for optimized re-renders)
// =============================================================================

export const selectDateRange = (state: HealthStore) => state.dateRange;
export const selectViewMode = (state: HealthStore) => state.viewMode;
export const selectFilters = (state: HealthStore) => state.filters;
export const selectQuickAdd = (state: HealthStore) => state.quickAdd;
export const selectMedicationFilters = (state: HealthStore) => state.medicationFilters;

// Check if any filters are active
export const selectHasActiveFilters = (state: HealthStore) => {
  const { filters, selectedLogType } = state;
  return (
    selectedLogType !== null ||
    filters.logTypes.length > 0 ||
    filters.severityMin !== null ||
    filters.severityMax !== null ||
    filters.searchQuery !== '' ||
    filters.showOnlyWithNotes ||
    filters.showOnlyWithAttachments
  );
};

// Get filter count for badge display
export const selectActiveFilterCount = (state: HealthStore) => {
  let count = 0;
  const { filters, selectedLogType } = state;
  
  if (selectedLogType) count++;
  if (filters.logTypes.length > 0) count++;
  if (filters.severityMin !== null || filters.severityMax !== null) count++;
  if (filters.searchQuery) count++;
  if (filters.showOnlyWithNotes) count++;
  if (filters.showOnlyWithAttachments) count++;
  
  return count;
};

